import { useEffect, useMemo, useRef, useState } from 'react';
import type { PinnedAgent } from '../lib/pinnedAgents';
import { AUTO_AGENT, EDITABLE_BUILTIN_PIN_IDS } from '../lib/pinnedAgents';
import type { AltYUser } from '../types/global';
import { listAltYUsers } from '../lib/altYApi';
import { useIsMobile } from '../lib/useIsMobile';

type ParticipantsPickerProps = {
  agents: PinnedAgent[];
  selectedAgentId: string | null;
  selectedUserIds: string[];
  expanded: boolean;
  onSelectAgent: (id: string) => void;
  onSelectUsers: (ids: string[]) => void;
  onEditAgent?: (id: string) => void;
  onDeleteAgent?: (id: string) => void;
};

function isEditable(id: string): boolean {
  return id.startsWith('user-') || EDITABLE_BUILTIN_PIN_IDS.has(id);
}

// Single combined dropdown listing every agent + every system user, multi-select.
// Agents are radio-like (the conversation routes to one), users are checkboxes
// (a message can be broadcast to all selected users). Picking an agent clears
// the user selection and vice versa — same mutual-exclusion App.tsx already had
// for the old single-user picker.
export function ParticipantsPicker({
  agents,
  selectedAgentId,
  selectedUserIds,
  expanded,
  onSelectAgent,
  onSelectUsers,
  onEditAgent,
  onDeleteAgent,
}: ParticipantsPickerProps) {
  const isMobile = useIsMobile();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [users, setUsers] = useState<AltYUser[]>([]);
  const [highlightIndex, setHighlightIndex] = useState(0);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const listRef = useRef<HTMLUListElement | null>(null);

  useEffect(() => {
    void listAltYUsers().then((list) => setUsers(list || []));
  }, []);

  const agentsWithAuto = useMemo(() => [AUTO_AGENT, ...agents], [agents]);

  const selectedAgent = useMemo(
    () => agentsWithAuto.find((a) => a.id === selectedAgentId) ?? AUTO_AGENT,
    [agentsWithAuto, selectedAgentId],
  );

  const userById = useMemo(() => {
    const map = new Map<string, AltYUser>();
    for (const u of users) map.set(u.id, u);
    return map;
  }, [users]);

  // Flattened list with section markers, so a single ArrowDown/Up loop can
  // navigate across both groups and rendering keeps the headers + items in
  // sync with the filtered view.
  type Row =
    | { kind: 'header'; key: string; label: string }
    | { kind: 'agent'; key: string; agent: PinnedAgent }
    | { kind: 'user'; key: string; user: AltYUser };

  const rows = useMemo<Row[]>(() => {
    const q = query.trim().toLowerCase();
    const matchedAgents = agentsWithAuto.filter((a) => {
      if (!q) return true;
      const label = (a.label || '').toLowerCase();
      const title = (a.flow?.title || '').toLowerCase();
      return label.includes(q) || title.includes(q);
    });
    const matchedUsers = users.filter((u) => {
      if (!q) return true;
      const label = (u.label || '').toLowerCase();
      const email = (u.email || '').toLowerCase();
      return label.includes(q) || email.includes(q);
    });
    const out: Row[] = [];
    if (matchedAgents.length) {
      out.push({ kind: 'header', key: 'h-agents', label: 'Agents' });
      for (const a of matchedAgents) out.push({ kind: 'agent', key: `a-${a.id}`, agent: a });
    }
    if (matchedUsers.length) {
      out.push({ kind: 'header', key: 'h-users', label: 'Users' });
      for (const u of matchedUsers) out.push({ kind: 'user', key: `u-${u.id}`, user: u });
    }
    return out;
  }, [agentsWithAuto, users, query]);

  // Items the highlight cursor can land on (skip headers).
  const selectableIndexes = useMemo(
    () => rows.map((r, i) => (r.kind === 'header' ? -1 : i)).filter((i) => i >= 0),
    [rows],
  );

  useEffect(() => {
    if (!selectableIndexes.length) {
      setHighlightIndex(0);
      return;
    }
    if (!selectableIndexes.includes(highlightIndex)) {
      setHighlightIndex(selectableIndexes[0]);
    }
  }, [selectableIndexes, highlightIndex]);

  useEffect(() => {
    if (!open) return;
    function onDown(event: MouseEvent) {
      const node = containerRef.current;
      if (node && event.target instanceof Node && !node.contains(event.target)) {
        setOpen(false);
      }
    }
    window.addEventListener('mousedown', onDown);
    return () => window.removeEventListener('mousedown', onDown);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const list = listRef.current;
    if (!list) return;
    const item = list.children[highlightIndex] as HTMLElement | undefined;
    item?.scrollIntoView({ block: 'nearest' });
  }, [open, highlightIndex]);

  function openMenu() {
    setOpen(true);
    setQuery('');
    // Land the highlight on whichever row matches the current selection — first
    // selected user wins (it's the most recently relevant), else the agent.
    const idx = (() => {
      if (selectedUserIds.length) {
        const i = rows.findIndex((r) => r.kind === 'user' && selectedUserIds.includes(r.user.id));
        if (i >= 0) return i;
      }
      if (selectedAgentId) {
        const i = rows.findIndex((r) => r.kind === 'agent' && r.agent.id === selectedAgentId);
        if (i >= 0) return i;
      }
      return selectableIndexes[0] ?? 0;
    })();
    setHighlightIndex(idx);
    if (!isMobile) {
      window.setTimeout(() => inputRef.current?.focus(), 0);
    }
  }

  function pickAgent(id: string) {
    onSelectAgent(id);
    setOpen(false);
    setQuery('');
  }

  function toggleUser(id: string) {
    const set = new Set(selectedUserIds);
    if (set.has(id)) set.delete(id);
    else set.add(id);
    onSelectUsers(Array.from(set));
    // Leave the menu open — multi-select means picking several in a row.
  }

  function moveHighlight(delta: 1 | -1) {
    if (!selectableIndexes.length) return;
    const at = selectableIndexes.indexOf(highlightIndex);
    const nextPos = at < 0
      ? (delta > 0 ? 0 : selectableIndexes.length - 1)
      : Math.min(selectableIndexes.length - 1, Math.max(0, at + delta));
    setHighlightIndex(selectableIndexes[nextPos]);
  }

  function handleKeyDown(event: React.KeyboardEvent<HTMLInputElement>) {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      moveHighlight(1);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      moveHighlight(-1);
    } else if (event.key === 'Enter') {
      event.preventDefault();
      const target = rows[highlightIndex];
      if (!target) return;
      if (target.kind === 'agent') pickAgent(target.agent.id);
      else if (target.kind === 'user') toggleUser(target.user.id);
    } else if (event.key === 'Escape') {
      event.preventDefault();
      setOpen(false);
    }
  }

  // Trigger label/icon: a selected agent wins visually when no users picked;
  // otherwise show user count (or single user's label) since user-mode bypasses
  // the agent.
  const triggerIcon = selectedUserIds.length
    ? 'fa-user'
    : (selectedAgent.icon || 'fa-search');
  const triggerLabel = (() => {
    if (selectedUserIds.length === 1) {
      const u = userById.get(selectedUserIds[0]);
      return u?.label || u?.email || '1 user';
    }
    if (selectedUserIds.length > 1) return `${selectedUserIds.length} users`;
    return selectedAgent.label;
  })();
  const triggerTitle = selectedUserIds.length
    ? `Recipients: ${selectedUserIds.map((id) => userById.get(id)?.label || id).join(', ')}`
    : `Active agent: ${selectedAgent.label}`;

  const menu = open ? (
    <div className="alty-agent-picker-menu">
      <input
        ref={inputRef}
        type="text"
        className="alty-agent-picker-input"
        placeholder="Search agents or users…"
        value={query}
        onChange={(e) => {
          setQuery(e.target.value);
          const first = selectableIndexes[0];
          if (first != null) setHighlightIndex(first);
        }}
        onKeyDown={handleKeyDown}
      />
      <ul ref={listRef} className="alty-agent-picker-list" role="listbox" aria-multiselectable="true">
        {rows.length === 0 ? (
          <li className="alty-agent-picker-empty">No matching agents or users</li>
        ) : rows.map((row, index) => {
          if (row.kind === 'header') {
            return (
              <li key={row.key} className="alty-agent-picker-section" aria-hidden="true">
                {row.label}
              </li>
            );
          }
          if (row.kind === 'agent') {
            const agent = row.agent;
            const checked = selectedAgentId === agent.id && selectedUserIds.length === 0;
            return (
              <li
                key={row.key}
                role="option"
                aria-selected={checked}
                className={
                  'alty-agent-picker-option'
                  + (index === highlightIndex ? ' highlight' : '')
                  + (checked ? ' selected' : '')
                }
                onMouseEnter={() => setHighlightIndex(index)}
                onMouseDown={(e) => {
                  e.preventDefault();
                  pickAgent(agent.id);
                }}
              >
                <span
                  className={
                    'alty-picker-radio' + (checked ? ' checked' : '')
                  }
                  aria-hidden="true"
                />
                <i className={`fas ${agent.icon}`} aria-hidden="true" />
                <span className="alty-agent-picker-option-label">{agent.label}</span>
                {onEditAgent && isEditable(agent.id) ? (
                  <span
                    role="button"
                    tabIndex={-1}
                    className="alty-agent-picker-edit"
                    title="Open the diagram + chat that created this agent"
                    aria-label={`Edit ${agent.label}`}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      onEditAgent(agent.id);
                      setOpen(false);
                      setQuery('');
                    }}
                  >
                    <i className="fas fa-pen" aria-hidden="true" />
                  </span>
                ) : null}
                {onDeleteAgent && agent.id.startsWith('user-') ? (
                  <span
                    role="button"
                    tabIndex={-1}
                    className="alty-agent-picker-delete"
                    title="Delete this agent"
                    aria-label={`Delete ${agent.label}`}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      onDeleteAgent(agent.id);
                    }}
                  >
                    <i className="fas fa-times" aria-hidden="true" />
                  </span>
                ) : null}
              </li>
            );
          }
          const user = row.user;
          const checked = selectedUserIds.includes(user.id);
          return (
            <li
              key={row.key}
              role="option"
              aria-selected={checked}
              className={
                'alty-agent-picker-option'
                + (index === highlightIndex ? ' highlight' : '')
                + (checked ? ' selected' : '')
              }
              onMouseEnter={() => setHighlightIndex(index)}
              onMouseDown={(e) => {
                e.preventDefault();
                toggleUser(user.id);
              }}
            >
              <span
                className={
                  'alty-picker-check' + (checked ? ' checked' : '')
                }
                aria-hidden="true"
              >
                {checked ? <i className="fas fa-check" aria-hidden="true" /> : null}
              </span>
              <i className="fas fa-user" aria-hidden="true" />
              <span className="alty-agent-picker-option-label">{user.label || user.email}</span>
            </li>
          );
        })}
      </ul>
    </div>
  ) : null;

  if (!expanded) {
    return (
      <div className="alty-agent-picker collapsed" ref={containerRef}>
        <button
          type="button"
          className={`alty-agent-picker-trigger collapsed ${selectedUserIds.length ? 'active' : ''}`}
          title={triggerTitle}
          onClick={() => (open ? setOpen(false) : openMenu())}
          aria-expanded={open}
        >
          <i className={`fas ${triggerIcon}`} aria-hidden="true" />
          {selectedUserIds.length > 1 ? (
            <span className="alty-picker-badge">{selectedUserIds.length}</span>
          ) : null}
        </button>
        {menu}
      </div>
    );
  }

  return (
    <div className="alty-agent-picker" ref={containerRef}>
      <button
        type="button"
        className={`alty-agent-picker-trigger ${selectedUserIds.length ? 'active' : ''}`}
        title={triggerTitle}
        onClick={() => (open ? setOpen(false) : openMenu())}
        aria-expanded={open}
      >
        <i className={`fas ${triggerIcon}`} aria-hidden="true" />
        <span className="alty-agent-picker-value">{triggerLabel}</span>
        <i className="fas fa-caret-down alty-agent-picker-caret" aria-hidden="true" />
      </button>
      {menu}
    </div>
  );
}
